import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gsap } from 'gsap';
import './lvl1.css';
import Levelinfo from '../components/layout/levelinfo';
import pinkChar from '../assets/playingcharacterpink.png';
import { useSound } from '../hooks/useSound';
import { useProgress } from '../hooks/useProgress';
import { getLevelConfig } from '../utils/levelConfig';
import { generateGrid, getColorStyle } from '../utils/randomGrid';

const NUM_COLORS = 6;
const MOVE_LIMIT = 15;

const MESSAGES = [
  'Tap a prism to start!',
  'Good job! Keep going! 🎉',
  'Nice move!',
  'Almost there, keep it up!',
  "You're on fire! 🔥",
  'Great placement!',
  'Getting closer!',
  'Looking good!',
  'One step closer to victory!',
  "Don't stop now!",
  'So close! Keep going!',
  'Amazing! Nearly there!',
];

const Lvl1 = () => {
  const navigate = useNavigate();
  const { playHover, playClick, playDrop: playPlace, playVictory, playDefeat } = useSound();
  const { completeLevel } = useProgress();
  const config = getLevelConfig(1);

  const [closing,  setClosing]  = useState(false);
  const [showInfo, setShowInfo] = useState(true);

  const [showRemember,   setShowRemember]   = useState(false);
  const [hidingRemember, setHidingRemember] = useState(false);

  const [gameStarted, setGameStarted] = useState(false);

  /* random grid pair — regenerated on mount and every reset */
  const [grid, setGrid] = useState(() => generateGrid(NUM_COLORS));
  const target = grid.target;
  const [playerGrid, setPlayerGrid] = useState(grid.player);

  const [selected,    setSelected]    = useState(null);
  const [moves,       setMoves]       = useState(0);
  const [msgIndex,    setMsgIndex]    = useState(0);
  const [msgPop,      setMsgPop]      = useState(false);
  const [flashMatch,  setFlashMatch]  = useState(false);
  const [flashDef,    setFlashDef]    = useState(false);
  const [moveFlash,   setMoveFlash]   = useState(false);
  const [gameOver,    setGameOver]    = useState(false);

  const contRef = useRef(null);

  const startPuzzle = () => {
    playClick();
    setClosing(true);
    setTimeout(() => {
      setShowInfo(false);
      setShowRemember(true);
    }, 800);
  };

  useEffect(() => {
    if (!showRemember) return;
    const t = setTimeout(() => {
      setHidingRemember(true);
      setTimeout(() => {
        setShowRemember(false);
        setHidingRemember(false);
        setGameStarted(true);
      }, 500);
    }, 5000);
    return () => clearTimeout(t);
  }, [showRemember]);

  useEffect(() => {
    if (showInfo || !contRef.current) return;
    gsap.fromTo(
      contRef.current,
      { opacity: 0, y: 20, scale: 0.97 },
      { opacity: 1, y: 0,  scale: 1, duration: 0.9, ease: 'power3.out', delay: 0.05 }
    );
  }, [showInfo]);

  const checkResult = useCallback((gridState, newMoves) => {
    const win = target.every((t, i) => gridState[i] === t);

    if (win) {
      const earnedStars = newMoves <= 10 ? 3 : newMoves <= 15 ? 2 : 1;
      const earnedCoins = config.baseCoins + earnedStars * 5;
      completeLevel(1, earnedStars, earnedCoins);

      setGameOver(true);
      setFlashMatch(true);
      playVictory();
      setTimeout(() => navigate('/victory', {
        state: { level: 1, stars: earnedStars, coins: earnedCoins, replayPath: '/level1' }
      }), 1400);
      return true;
    }

    if (newMoves >= MOVE_LIMIT) {
      setGameOver(true);
      setFlashDef(true);
      playDefeat();
      setTimeout(() => navigate('/defeat', {
        state: { level: 1, replayPath: '/level1' }
      }), 1400);
      return true;
    }
    return false;
  }, [navigate, playVictory, playDefeat, completeLevel, config, target]);

  const bumpMessage = useCallback(() => {
    setMsgIndex(i => {
      const next = (i + 1) % MESSAGES.length;
      return next === 0 ? 1 : next;
    });
    setMsgPop(true);
    setTimeout(() => setMsgPop(false), 400);
  }, []);

  const handleCellClick = (idx) => {
    if (!gameStarted || gameOver) return;
    playClick();

    if (selected === null) {
      if (playerGrid[idx] !== null) {
        setSelected(idx);
      }
    } else {
      if (selected === idx) {
        setSelected(null);
        return;
      }
      const newGrid = [...playerGrid];
      const temp = newGrid[selected];
      newGrid[selected] = newGrid[idx];
      newGrid[idx] = temp;

      const newMoves = moves + 1;
      setPlayerGrid(newGrid);
      setSelected(null);
      setMoves(newMoves);
      playPlace();
      bumpMessage();

      setMoveFlash(true);
      setTimeout(() => setMoveFlash(false), 300);

      checkResult(newGrid, newMoves);
    }
  };

  const handleReset = () => {
    if (gameOver) return;
    playClick();
    const fresh = generateGrid(NUM_COLORS); // new colors + new layout every reset
    setGrid(fresh);
    setPlayerGrid(fresh.player);
    setSelected(null);
    setMoves(0);
    setMsgIndex(0);
    if (contRef.current) {
      gsap.fromTo(
        contRef.current,
        { scale: 0.96, opacity: 0.5 },
        { scale: 1, opacity: 1, duration: 0.55, ease: 'back.out(1.7)' }
      );
    }
  };

  const handleExit = () => {
    playClick();
    navigate(-1);
  };

  const renderPrism = (colorId) => {
    if (!colorId) return null;
    const c = getColorStyle(colorId);
    if (!c) return null;
    return (
      <div
        className="lvl1-prism"
        style={{
          background: c.bg,
          boxShadow: `0 0 18px ${c.glow}55, inset 0 1px 0 rgba(255,255,255,0.2)`,
        }}
      />
    );
  };

  return (
    <main className="level1">

      {showInfo && (
        <Levelinfo
          closing={closing}
          onStart={startPuzzle}
          missionchar="missionchar1"
          mission="missionpink"
          missiontitle="missiontitlepink"
          substyle="missionsubpink"
          sub="Match the target pattern by swapping colored prisms!"
          infostyle="missionipink"
          info="Tap a colored prism, then tap where you want to move it. Arrange all prisms to match the secret pattern — in 15 moves or fewer! The pattern is different every time you play."
        />
      )}

      {showRemember && (
        <div className={`lvl1-remember-overlay ${hidingRemember ? 'hiding' : ''}`}>
          <div className="lvl1-remember-box">
            <div className="lvl1-remember-title">Remember the order!</div>
            <div className="lvl1-remember-grid">
              {target.map((colorId, i) => {
                const c = colorId ? getColorStyle(colorId) : null;
                return (
                  <div
                    key={i}
                    className={`lvl1-remember-cell ${!colorId ? 'empty' : ''}`}
                    style={c ? { background: c.bg, boxShadow: `0 0 22px ${c.glow}66` } : {}}
                  />
                );
              })}
            </div>
            <div className="lvl1-timer-bar-wrap">
              <div className="lvl1-timer-bar" />
            </div>
          </div>
        </div>
      )}

      {flashMatch && <div className="lvl1-match-flash" />}
      {flashDef   && <div className="lvl1-defeat-flash" />}

      <section className="level1cont" ref={contRef}>

        <div className="lvl1-topbar">
          <button className="lvl1-exit-btn" onClick={handleExit} onMouseEnter={playHover}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
            EXIT
          </button>

          <div className="lvl1-title-block">
            <h1>LEVEL 1</h1>
            <div className={`lvl1-moves ${moveFlash ? 'flash' : ''}`}>
              Moves: <span>{moves}</span> <span className="lvl1-moves-limit">/ {MOVE_LIMIT}</span>
            </div>
          </div>

          <div className="lvl1-topright">
            <button className="lvl1-reset-btn" onClick={handleReset} onMouseEnter={playHover}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                <path d="M3 3v5h5"/>
              </svg>
              RESET
            </button>
            <button className="lvl1-icon-btn" onMouseEnter={playHover} onClick={playClick} title="Music">♪</button>
            <button className="lvl1-icon-btn" onMouseEnter={playHover} onClick={playClick} title="Settings">◉</button>
          </div>
        </div>

        <div className="lvl1-left">
          <div className="lvl1-character-wrap">
            <img src={pinkChar} alt="Prisma" className="lvl1-char-img" />
            <div className="lvl1-char-bubble">
              <div className="lvl1-char-name">PRISMA</div>
              <div className={`lvl1-char-msg ${msgPop ? 'msg-pop' : ''}`}>
                {MESSAGES[msgIndex]}
              </div>
            </div>
          </div>
        </div>

        <div className="lvl1-center">
          <div className="lvl1-grid-label">YOUR GRID</div>
          <div className="lvl1-grid-container">
            <div className="lvl1-grid">
              {playerGrid.map((colorId, i) => (
                <div
                  key={i}
                  className={`lvl1-player-cell ${colorId ? 'has-prism' : ''} ${selected === i ? 'selected' : ''}`}
                  onClick={() => handleCellClick(i)}
                  onMouseEnter={playHover}
                >
                  {renderPrism(colorId)}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lvl1-right">
          <div className="lvl1-panel">
            <div className="lvl1-panel-title">HOW TO PLAY</div>
            <ul className="lvl1-howto-list">
              <li>Tap any colored prism to select it</li>
              <li>Tap another cell to swap or move it there</li>
              <li>Recreate the secret pattern you saw at the start</li>
              <li>Complete in 15 moves or fewer to win!</li>
            </ul>
          </div>

          <div className="lvl1-panel">
            <div className="lvl1-panel-title">STARS</div>
            <div className="lvl1-stars-list">
              <div className="lvl1-star-row">
                <span>Under 10 moves</span>
                <span className="lvl1-star-icon" style={{ color: '#FFD930' }}>★</span>
              </div>
              <div className="lvl1-star-row">
                <span>Under 15 moves</span>
                <span className="lvl1-star-icon" style={{ color: '#18B8E8' }}>★</span>
              </div>
              <div className="lvl1-star-row">
                <span>Complete level</span>
                <span className="lvl1-star-icon" style={{ color: '#FF2D9A' }}>★</span>
              </div>
            </div>
          </div>
        </div>

      </section>
    </main>
  );
};

export default Lvl1;